import { Injectable, EventEmitter } from '@angular/core';

export interface ShortcutModifiers {
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean; // Cmd on macOS
}

export interface ShortcutDefinition {
  id: string;
  label: string;
  category: 'Navigation' | 'View';
  defaultKey: string;
  currentKey: string;
  defaultModifiers: ShortcutModifiers;
  currentModifiers: ShortcutModifiers;
  enabled: boolean;
}

export interface ShortcutConflict {
  key: string;
  shortcuts: string[]; // IDs of conflicting shortcuts
}

// Helper to create empty modifiers
export function emptyModifiers(): ShortcutModifiers {
  return { ctrl: false, alt: false, shift: false, meta: false };
}

// Helper to check if modifiers match
export function modifiersMatch(a: ShortcutModifiers, b: ShortcutModifiers): boolean {
  return a.ctrl === b.ctrl && a.alt === b.alt && a.shift === b.shift && a.meta === b.meta;
}

// Helper to check if any modifier is set
export function hasModifiers(m: ShortcutModifiers): boolean {
  return m.ctrl || m.alt || m.shift || m.meta;
}

const STORAGE_KEY = 'infinitform_keyboard_shortcuts';

@Injectable({
  providedIn: 'root'
})
export class KeyboardShortcutsService {
  // Event emitted when shortcuts change
  public shortcutsChanged = new EventEmitter<ShortcutDefinition[]>();

  // Detect macOS for display purposes
  public readonly isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);

  /**
   * Check if a keyboard event should be suppressed because the user is typing in an input field.
   * Use this method in all keyboard shortcut handlers to prevent shortcuts from firing while typing.
   *
   * @param event - The keyboard event to check
   * @returns true if the shortcut should be suppressed, false if it should be processed
   */
  shouldSuppressShortcut(event: KeyboardEvent): boolean {
    // Check the event target
    const target = event.target as HTMLElement;
    if (target) {
      const tagName = target.tagName?.toUpperCase();
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
        return true;
      }
      if (target.isContentEditable) {
        return true;
      }
    }

    // Check the currently focused element (more reliable for some cases)
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) {
      const activeTagName = activeElement.tagName?.toUpperCase();
      if (activeTagName === 'INPUT' || activeTagName === 'TEXTAREA' || activeTagName === 'SELECT') {
        return true;
      }
      if (activeElement.isContentEditable) {
        return true;
      }

      // Check for custom Angular form components
      let parent: HTMLElement | null = activeElement;
      while (parent) {
        const parentTag = parent.tagName?.toLowerCase();
        if (
          parentTag === 'app-input' ||
          parentTag === 'app-form-control' ||
          parentTag === 'app-select' ||
          parentTag === 'app-textarea' ||
          parentTag === 'app-modal' ||
          parentTag === 'app-cad-confirm-modal'
        ) {
          return true;
        }
        parent = parent.parentElement;
      }
    }

    return false;
  }

  // Default shortcuts configuration
  private readonly defaultShortcuts: ShortcutDefinition[] = [
    {
      id: 'home-view',
      label: 'Reset to home view',
      category: 'Navigation',
      defaultKey: 'H',
      currentKey: 'H',
      defaultModifiers: emptyModifiers(),
      currentModifiers: emptyModifiers(),
      enabled: true
    },
    {
      id: 'fit-all',
      label: 'Fit all in view',
      category: 'Navigation',
      defaultKey: 'A',
      currentKey: 'A',
      defaultModifiers: emptyModifiers(),
      currentModifiers: emptyModifiers(),
      enabled: true
    },
    {
      id: 'toggle-grid',
      label: 'Toggle grid',
      category: 'View',
      defaultKey: 'G',
      currentKey: 'G',
      defaultModifiers: emptyModifiers(),
      currentModifiers: emptyModifiers(),
      enabled: true
    },
    {
      id: 'toggle-dimensions',
      label: 'Toggle dimensions',
      category: 'View',
      defaultKey: 'D',
      currentKey: 'D',
      defaultModifiers: emptyModifiers(),
      currentModifiers: emptyModifiers(),
      enabled: true
    }
  ];

  private shortcuts: ShortcutDefinition[] = [];
  private readonly isDesktop = !!(window as any).electronAPI;

  constructor() {
    this.loadShortcuts();
  }

  /**
   * Load shortcuts from localStorage, falling back to defaults
   */
  private loadShortcuts(): void {
    if (this.isDesktop) {
      // On desktop, start with defaults. NeDB data arrives later via importFromSync().
      this.shortcuts = this.defaultShortcuts.map(s => ({
        ...s,
        currentModifiers: { ...s.defaultModifiers }
      }));
      return;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<ShortcutDefinition>[];
        // Merge stored shortcuts with defaults (in case new shortcuts were added)
        this.shortcuts = this.defaultShortcuts.map(defaultShortcut => {
          const storedItem = parsed.find(s => s.id === defaultShortcut.id);
          if (storedItem) {
            return {
              ...defaultShortcut,
              currentKey: storedItem.currentKey || defaultShortcut.defaultKey,
              currentModifiers: storedItem.currentModifiers
                ? { ...emptyModifiers(), ...storedItem.currentModifiers }
                : { ...defaultShortcut.defaultModifiers },
              enabled: storedItem.enabled !== undefined ? storedItem.enabled : true
            };
          }
          return { ...defaultShortcut, currentModifiers: { ...defaultShortcut.defaultModifiers } };
        });
      } else {
        this.shortcuts = this.defaultShortcuts.map(s => ({
          ...s,
          currentModifiers: { ...s.defaultModifiers }
        }));
      }
    } catch {
      this.shortcuts = this.defaultShortcuts.map(s => ({
        ...s,
        currentModifiers: { ...s.defaultModifiers }
      }));
    }
  }

  /**
   * Save shortcuts to localStorage
   */
  private saveShortcuts(): void {
    if (this.isDesktop) return;

    const toStore = this.shortcuts.map(s => ({
      id: s.id,
      currentKey: s.currentKey,
      currentModifiers: s.currentModifiers,
      enabled: s.enabled
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  }

  /**
   * Get all shortcuts
   */
  getShortcuts(): ShortcutDefinition[] {
    return this.shortcuts.map(s => ({ ...s }));
  }

  /**
   * Get shortcuts grouped by category
   */
  getShortcutsByCategory(): Map<string, ShortcutDefinition[]> {
    const grouped = new Map<string, ShortcutDefinition[]>();
    for (const shortcut of this.shortcuts) {
      const existing = grouped.get(shortcut.category) || [];
      existing.push({ ...shortcut });
      grouped.set(shortcut.category, existing);
    }
    return grouped;
  }

  /**
   * Get a shortcut by ID
   */
  getShortcut(id: string): ShortcutDefinition | undefined {
    const shortcut = this.shortcuts.find(s => s.id === id);
    return shortcut ? { ...shortcut } : undefined;
  }

  /**
   * Get the current key for a shortcut
   */
  getKey(id: string): string | undefined {
    const shortcut = this.shortcuts.find(s => s.id === id);
    return shortcut?.enabled ? shortcut.currentKey : undefined;
  }

  /**
   * Check if a key event matches a shortcut (case-insensitive, with modifiers)
   */
  isKey(id: string, pressedKey: string, event?: KeyboardEvent): boolean {
    const shortcut = this.shortcuts.find(s => s.id === id);
    if (!shortcut || !shortcut.enabled) return false;

    const keyMatches = shortcut.currentKey.toUpperCase() === pressedKey.toUpperCase();
    if (!keyMatches) return false;

    // If no event provided, only check key (backward compatibility)
    if (!event) return true;

    // Check modifiers match
    const eventModifiers: ShortcutModifiers = {
      ctrl: event.ctrlKey,
      alt: event.altKey,
      shift: event.shiftKey,
      meta: event.metaKey
    };

    return modifiersMatch(shortcut.currentModifiers, eventModifiers);
  }

  /**
   * Check if a keyboard event matches a shortcut (convenience method)
   */
  matchesEvent(id: string, event: KeyboardEvent): boolean {
    return this.isKey(id, event.key, event);
  }

  /**
   * Update a shortcut's key and modifiers
   */
  updateShortcut(id: string, newKey: string, modifiers?: ShortcutModifiers): boolean {
    const shortcut = this.shortcuts.find(s => s.id === id);
    if (!shortcut) return false;

    shortcut.currentKey = newKey.toUpperCase();
    if (modifiers) {
      shortcut.currentModifiers = { ...modifiers };
    }
    this.saveShortcuts();
    this.shortcutsChanged.emit(this.getShortcuts());
    return true;
  }

  /**
   * Toggle a shortcut's enabled state
   */
  toggleShortcut(id: string, enabled: boolean): boolean {
    const shortcut = this.shortcuts.find(s => s.id === id);
    if (!shortcut) return false;

    shortcut.enabled = enabled;
    this.saveShortcuts();
    this.shortcutsChanged.emit(this.getShortcuts());
    return true;
  }

  /**
   * Check for conflicts (same key+modifiers used by multiple enabled shortcuts)
   */
  getConflicts(): ShortcutConflict[] {
    const keyMap = new Map<string, string[]>();

    for (const shortcut of this.shortcuts) {
      if (!shortcut.enabled) continue;
      // Create a unique key string including modifiers
      const keyCombo = this.getShortcutComboString(shortcut);
      const existing = keyMap.get(keyCombo) || [];
      existing.push(shortcut.id);
      keyMap.set(keyCombo, existing);
    }

    const conflicts: ShortcutConflict[] = [];
    keyMap.forEach((shortcuts, key) => {
      if (shortcuts.length > 1) {
        conflicts.push({ key, shortcuts });
      }
    });

    return conflicts;
  }

  /**
   * Get a unique string representation of shortcut combo (for conflict detection)
   */
  private getShortcutComboString(shortcut: ShortcutDefinition): string {
    const parts: string[] = [];
    if (shortcut.currentModifiers.ctrl) parts.push('CTRL');
    if (shortcut.currentModifiers.alt) parts.push('ALT');
    if (shortcut.currentModifiers.shift) parts.push('SHIFT');
    if (shortcut.currentModifiers.meta) parts.push('META');
    parts.push(shortcut.currentKey.toUpperCase());
    return parts.join('+');
  }

  /**
   * Check if there are any conflicts
   */
  hasConflicts(): boolean {
    return this.getConflicts().length > 0;
  }

  /**
   * Reset all shortcuts to defaults
   */
  resetToDefaults(): void {
    this.shortcuts = this.defaultShortcuts.map(s => ({
      ...s,
      currentModifiers: { ...s.defaultModifiers }
    }));
    this.saveShortcuts();
    this.shortcutsChanged.emit(this.getShortcuts());
  }

  /**
   * Reset a single shortcut to its default
   */
  resetShortcut(id: string): boolean {
    const shortcut = this.shortcuts.find(s => s.id === id);
    const defaultShortcut = this.defaultShortcuts.find(s => s.id === id);
    if (!shortcut || !defaultShortcut) return false;

    shortcut.currentKey = defaultShortcut.defaultKey;
    shortcut.currentModifiers = { ...defaultShortcut.defaultModifiers };
    shortcut.enabled = true;
    this.saveShortcuts();
    this.shortcutsChanged.emit(this.getShortcuts());
    return true;
  }

  /**
   * Export shortcuts for backend sync
   */
  exportForSync(): object {
    return {
      shortcuts: this.shortcuts.map(s => ({
        id: s.id,
        currentKey: s.currentKey,
        currentModifiers: s.currentModifiers,
        enabled: s.enabled
      }))
    };
  }

  /**
   * Import shortcuts from backend
   */
  importFromSync(data: any): void {
    // Handle both formats: { shortcuts: [...] } or direct array [...]
    const shortcuts = Array.isArray(data) ? data : data?.shortcuts;
    if (!Array.isArray(shortcuts)) return;

    for (const item of shortcuts) {
      const shortcut = this.shortcuts.find(s => s.id === item.id);
      if (shortcut) {
        if (item.currentKey) shortcut.currentKey = item.currentKey.toUpperCase();
        if (item.currentModifiers) {
          shortcut.currentModifiers = { ...emptyModifiers(), ...item.currentModifiers };
        }
        if (item.enabled !== undefined) shortcut.enabled = item.enabled;
      }
    }

    this.saveShortcuts();
    this.shortcutsChanged.emit(this.getShortcuts());
  }

  /**
   * Format shortcut for display (platform-aware)
   */
  formatShortcutDisplay(shortcut: ShortcutDefinition): string {
    const parts: string[] = [];
    const m = shortcut.currentModifiers;

    if (this.isMac) {
      // macOS style: ⌃⌥⇧⌘ + Key
      if (m.ctrl) parts.push('⌃');
      if (m.alt) parts.push('⌥');
      if (m.shift) parts.push('⇧');
      if (m.meta) parts.push('⌘');
    } else {
      // Windows/Linux style: Ctrl+Alt+Shift+Key
      if (m.ctrl) parts.push('Ctrl');
      if (m.alt) parts.push('Alt');
      if (m.shift) parts.push('Shift');
      if (m.meta) parts.push('Win');
    }

    // Format the key
    let key = shortcut.currentKey;
    if (key === 'SPACE') key = 'Space';
    else if (key === 'ARROWUP') key = '↑';
    else if (key === 'ARROWDOWN') key = '↓';
    else if (key === 'ARROWLEFT') key = '←';
    else if (key === 'ARROWRIGHT') key = '→';

    parts.push(key);

    return this.isMac ? parts.join('') : parts.join('+');
  }

  /**
   * Check if a shortcut has been modified from its default
   */
  isModified(shortcut: ShortcutDefinition): boolean {
    const defaultShortcut = this.defaultShortcuts.find(s => s.id === shortcut.id);
    if (!defaultShortcut) return false;

    return shortcut.currentKey !== defaultShortcut.defaultKey ||
           !modifiersMatch(shortcut.currentModifiers, defaultShortcut.defaultModifiers) ||
           !shortcut.enabled;
  }
}
